/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeAutocomplete,
  BaSeButton,
  BaSeGrid,
  BaSeGridItem,
  BaSeHeading6,
  BaSeInput,
  BaSeOptionPicker,
  BaSeParagraph,
  BaSeTag,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useCityController } from '../../../../../hooks/controllers/city-controller';
import { useUfController } from '../../../../../hooks/controllers/uf-controller';
import { useMessage } from '../../../../../hooks/messages/useMessage';
import { useManageClassService } from '../../../../../hooks/services/manage-class-service';
import { useRegionalService } from '../../../../../hooks/services/regional-service';
import { Container, ContainerButton, ContentList } from './regional-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

function EditRegional({ closeModal, fetchRefional, data }) {
  const { getUfs } = useUfController();
  const { getCitiesByUf, rawCities } = useCityController();
  const { updateReginal } = useRegionalService();
  const { handleAttention, handleError, handleSuccess } = useMessage();
  const { hasRole } = Sebrae.useAuth();

  const ufDisabled =
    cache.getItem(cacheKeySebrae) !== 'NA' || !hasRole('ADMINISTRADOR');

  const { getManagerbYiDuF } = useManageClassService();

  const [listUfs, setListUfs] = React.useState([]);
  const [ufSelected, setUfSelected] = React.useState([data.uf] ?? []);
  const [listCitiesSelected, setListCitiesSelected] = React.useState(
    (data.cities ?? []).map((city) => ({
      ...city,
      label: city.name,
    })),
  );
  const [listManagersSelected, setListManagersSelected] = React.useState(
    (data.responsibleManagers ?? []).map((manager) => ({
      ...manager,
      label: manager.name,
    })),
  );

  const [regionalName, setRegionalName] = React.useState(data.name);
  const [handleConfirmSave, setHandleConfirmSave] = React.useState(false);
  const [saveMode, setSaveMode] = React.useState('');
  const [nameIsValid, setNameIsValid] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const response = await getUfs();
        setListUfs(response);
        const ufDef = await response.find(
          (res) => res.sigla === cache.getItem(cacheKeySebrae),
        );
        if (cache.getItem(cacheKeySebrae) !== 'NA') {
          setUfSelected([ufDef]);
          return;
        }
        setUfSelected([
          {
            id: data.uf.id,
            label: data.uf.name,
          },
        ]);
      } catch (e) {
        setListUfs([]);
      }
    };
    if (data.uf) {
      getCitiesByUf({ id: data.uf.id });
    }
    load();
  }, [data.uf, getCitiesByUf, getUfs]);

  const handleAutompleteSearchManager = React.useCallback(
    async (term) => {
      if (term.trim().length < 3) return [];
      try {
        const response = await getManagerbYiDuF({
          pageIndex: 0,
          pageSize: 100,
          nameSearch: term,
          idUf: ufSelected[0].id,
        });

        const responseFormated = response.items.map((item) => {
          return {
            ...item,
            id: item.id,
            label: item.name,
          };
        });
        return responseFormated;
      } catch (error) {
        return [];
      }
    },
    [getManagerbYiDuF, ufSelected],
  );

  const handleSelectUf = (values) => {
    if (values.length === 0) return;
    getCitiesByUf({ id: values[0].id });
    setUfSelected(values);
  };

  const handleSelectChange = (values) => {
    setListCitiesSelected((prev) => {
      const newItems = values.filter(
        (val) => !prev.some((item) => item.id === val.id),
      );
      return [...prev, ...newItems];
    });
  };

  const handleRemoveTagCitie = (id) => {
    setListCitiesSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const handleRemoveTagmANAGER = (id) => {
    setListManagersSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const validateForm = React.useCallback(() => {
    return (
      regionalName.trim().length > 0 &&
      ufSelected.length > 0 &&
      listCitiesSelected.length > 0
    );
  }, [regionalName, ufSelected, listCitiesSelected]);

  const handleSave = React.useCallback(
    async (action) => {
      try {
        if (!validateForm()) {
          handleAttention('Campo obrigatório não informado.');
          return;
        }

        setHandleConfirmSave(true);
        setSaveMode(action);
      } catch (error) {
        setNameIsValid(null);
        handleError(error.message);
      }
    },
    [validateForm, handleAttention, handleError],
  );

  const confirmSaveRegional = React.useCallback(async () => {
    try {
      const ufId = ufSelected[0]?.id;
      const dto = {
        uf: ufId,
        cities: listCitiesSelected.map(({ id }) => id),
        name: regionalName,
        responsibleManagers: listManagersSelected.map(({ id }) => id),
      };

      await updateReginal(dto, data.id);
      handleSuccess('Operação realizada com sucesso.');
      closeModal(saveMode);
    } catch (error) {
      if (
        error.error === 'validate-regional-name-in-uf' ||
        error.errorCode === 'validate-regional-name-in-uf'
      ) {
        handleAttention(error.message);
        return;
      }
      handleError(error.message);
    }
  }, [
    ufSelected,
    listCitiesSelected,
    regionalName,
    listManagersSelected,
    updateReginal,
    data.id,
    handleSuccess,
    closeModal,
    saveMode,
    handleError,
    handleAttention,
  ]);

  if (!nameIsValid && handleConfirmSave) {
    return (
      <>
        <BaSeGrid gap={24}>
          <BaSeGridItem size={12}>
            <BaSeHeading6
              color={BaSeTheme.colors.feedbackInterface.atencao55}
              isBold={true}
            >
              Atenção!
            </BaSeHeading6>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">
              <strong>
                Tem certeza que deseja editar a regional {regionalName}?
              </strong>
            </BaSeParagraph>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <Container>
              <BaSeParagraph color="#636363">UF:</BaSeParagraph>
              <BaSeParagraph color="#8B99A7">
                {ufSelected[0].label}
              </BaSeParagraph>
            </Container>
          </BaSeGridItem>
          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Cidades:</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">
                {listCitiesSelected.map((item) => item.label).join(', ')}.
              </BaSeParagraph>
            </ContentList>
          </BaSeGridItem>

          {listManagersSelected.length > 0 && (
            <BaSeGridItem size={12}>
              <BaSeParagraph color="#636363">
                Gestores vinculados:
              </BaSeParagraph>
              <ContentList>
                <BaSeParagraph color="#8B99A7">
                  {listManagersSelected.map((item) => item.label).join(', ')}
                </BaSeParagraph>
              </ContentList>
            </BaSeGridItem>
          )}
        </BaSeGrid>

        <ContainerButton>
          <BaSeButton
            value="Confirmar"
            color={BaSeTheme.colors.defaultColors.black}
            onClick={() => {
              //setHandleConfirmSave((prev) => !prev);
              confirmSaveRegional();
            }}
          />
          <BaSeButton
            value="Revisar"
            type="secondary"
            onClick={() => {
              setHandleConfirmSave((prev) => !prev);
            }}
          />
        </ContainerButton>
      </>
    );
  }

  return (
    <>
      <BaSeGrid gap={24}>
        <BaSeGridItem size={12}>
          <BaSeInput
            label="Nome da regional:"
            onChange={setRegionalName}
            value={regionalName}
            maxLength={100}
            overrideColors={{
              border: nameIsValid ? '#ff0000' : '',
            }}
            complement={nameIsValid ? '' : ''}
            placeholder="Nome da regional"
          />
        </BaSeGridItem>
        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="UF:"
            maxMenuHeight="100px"
            maxOptionsSelected={1}
            isDisabled={ufDisabled}
            values={listUfs}
            initialSelectedValuesId={ufSelected.map((uf) => uf.id)}
            emptyValueLabel="Sem opção selecionada"
            onChange={handleSelectUf}
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Cidades:"
            maxMenuHeight="100px"
            maxOptionsSelected={1}
            values={rawCities}
            emptyValueLabel="Selecione uma ou mais cidades"
            searchPlaceholder="Selecione uma ou mais cidades"
            onChange={handleSelectChange}
            initialSelectedValuesId={listCitiesSelected.map((uf) => uf.id)}
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {listCitiesSelected.map((s) => (
              <BaSeTag
                key={s.id}
                color="default"
                limitedWidth={200}
                label={s.label}
                onClick={() => console.log('Tag clicada:', s)}
                onClose={() => handleRemoveTagCitie(s.id)}
                size="small"
                themeMode="light"
                type="primary"
              />
            ))}
          </div>
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            inputMode="search"
            label="Gestores vinculados:"
            minLengthToSearch={1}
            mode="list"
            isDisabled={ufSelected.length === 0 ? true : false}
            onSearchTerm={handleAutompleteSearchManager}
            onSelectedItem={(item, clear) => {
              setListManagersSelected((prevManagers) => {
                const existingIds = new Set(prevManagers.map((m) => m.id));
                if (!existingIds.has(item.id)) {
                  return [...prevManagers, item];
                }
                return prevManagers;
              });
              clear('');
            }}
            placeholder="Selecione um ou mais gestores"
            subLabel=""
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {listManagersSelected.map((s) => (
              <BaSeTag
                key={s.id}
                color="default"
                limitedWidth={200}
                label={s.name}
                onClick={() => console.log('Tag clicada:', s)}
                onClose={() => handleRemoveTagmANAGER(s.id)}
                size="small"
                themeMode="light"
                type="primary"
              />
            ))}
          </div>
        </BaSeGridItem>
      </BaSeGrid>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 30,
        }}
      >
        <BaSeButton
          value="Salvar edição"
          onClick={() => {
            handleSave('save');
          }}
        />
        <BaSeButton
          value="Cancelar"
          color={BaSeTheme.colors.defaultColors.black}
          type="secondary"
          onClick={() => {
            closeModal();
          }}
        />
      </div>
    </>
  );
}

export default EditRegional;
